import type { Ref } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useContentToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const text = val
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim()
    .toLowerCase();
  return text !== '' && text !== 'false' && text !== 'null' && text !== 'undefined';
}

/**
 * Short pricing disclaimer shown above the floor-plans ApartmentSync embed.
 * Bound to `custom_pricing_verbiage`; renders nothing when the field is empty.
 */
export function PricingVerbiage() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const pricingVerbiage = useContentToken('pricingverbiage.text');

  if (!hasValue(pricingVerbiage)) return null;

  return (
    <section
      ref={sectionRef as Ref<HTMLElement>}
      className="reveal-fade-up pt-8 pb-1 md:pt-12 md:pb-2 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="pricingverbiage.text"
          as="div"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PricingVerbiage',
  displayName: 'Pricing Verbiage',
  tokens: {
    'pricingverbiage.text': {
      label: 'Pricing Verbiage',
      type: 'richtext',
      defaultField: 'custom_pricing_verbiage',
      description:
        'Pricing disclaimer shown above the floor plans. Pulled from the property; the section is hidden when empty.',
    },
  },
};

(PricingVerbiage as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PricingVerbiage;
